import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

import DoubleDigitCountBubble from '../../DoubleDigitCountBubble';

const CellButton = styled.button<{ tint: 'white' | 'beige' }>`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 16px;
  border: 0;
  background: transparent;
  cursor: pointer;

  &.tint-white {
    border-right: 1px solid var(--stroke-light, #dddde2);
  }

  &.tint-beige {
    border-right: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &:hover,
  &[aria-pressed='true'] {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const AvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
`;

const AvatarImg = styled.img<{ size: 'lg' | 'md' }>`
  border-radius: 50%;
  object-fit: cover;

  &.size-lg {
    width: 40px;
    height: 40px;
  }

  &.size-md {
    width: 36px;
    height: 36px;
  }
`;

const AvatarPlaceholder = styled.span<{ size: 'lg' | 'md' }>`
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--brand-colors-beige-200, #f2f0ec);

  &.size-lg {
    width: 40px;
    height: 40px;
  }

  &.size-md {
    width: 36px;
    height: 36px;
  }
`;

const Badge = styled(DoubleDigitCountBubble)`
  position: absolute;
  top: -6px;
  right: -10px;
  box-sizing: border-box;
  width: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border: 1px solid var(--brand-colors-beige-200, #f2f0ec);
  border-radius: var(--border-radius-radius-round, 104px);
  background: var(--error-dark, #b60000);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--text-white, #ffffff);
`;

const DropdownCue = styled.span`
  position: absolute;
  right: -7px;
  bottom: -4px;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--brand-colors-beige-400, #ccc9bc);
  box-shadow: 0 0 0 2px var(--background-white, #ffffff);
  color: var(--text-primary, #141413);
  line-height: 1;
`;

export type AvatarCellProps = {
  avatarUrl: string;
  count: number;
  avatarSize: 36 | 40;
  tint: 'white' | 'beige';
  pressed?: boolean;
  onClick?: () => void;
};

const AvatarCell = ({
  avatarUrl,
  count,
  avatarSize,
  tint,
  pressed,
  onClick,
}: AvatarCellProps) => {
  const size = avatarSize === 40 ? 'lg' : 'md';

  return (
    <CellButton
      type="button"
      tint={tint}
      aria-label="Account menu"
      aria-pressed={pressed ?? false}
      onClick={onClick}
      data-testid="avatar-cell"
      data-avatar-size={avatarSize}
    >
      <AvatarWrap>
        {avatarUrl !== '' ? (
          <AvatarImg
            src={avatarUrl}
            alt=""
            size={size}
            data-testid="avatar-img"
          />
        ) : (
          <AvatarPlaceholder size={size} data-testid="avatar-placeholder" />
        )}
        <Badge count={count} />
        <DropdownCue data-testid="avatar-dropdown-cue" aria-hidden>
          <Icon name="keyboard_arrow_down" size={18} />
        </DropdownCue>
      </AvatarWrap>
    </CellButton>
  );
};

export default AvatarCell;
